import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Badge, Button, ProgressBar, Tab, Nav, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faEnvelope,
  faPhone,
  faCalendar,
  faCheckCircle,
  faClock,
  faPlay,
  faPause,
  faChartLine,
  faBolt,
  faUsers,
  faTasks,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

interface WorkflowTask {
  task_id: string;
  lead_id?: string;
  lead_first_name?: string;
  lead_last_name?: string;
  lead_email?: string;
  lead_score?: number;
  agent_type: 'response' | 'intelligence' | 'nurture' | 'monitor' | string;
  action: string;
  priority: 'low' | 'medium' | 'high' | 'urgent' | string;
  scheduled_for: string;
  status?: 'pending' | 'executing' | 'completed' | 'failed' | 'escalated';
  requires_approval: boolean;
  task_data?: any;
}

interface WorkflowInstance {
  instance_id: string;
  lead_id: string;
  user_id: string;
  workflow_template_id: string;
  current_stage_id: number;
  status: 'ACTIVE' | 'COMPLETED' | 'PAUSED';
  automation_level: 'manual' | 'supervised' | 'autonomous';
  metadata?: any;
  created_at: string;
  lead_first_name?: string;
  lead_last_name?: string;
  lead_name?: string;
  lead_email?: string;
  lead_phone?: string;
  lead_score?: number;
  property_type?: string;
  buying_timeline?: string;
  total_tasks?: number;
  pending_tasks?: number;
  completed_tasks?: number;
  next_task_date?: string;
  progress_percentage?: number;
  upcoming_tasks?: WorkflowTask[];
}

interface WorkflowStats {
  total_workflows: number;
  active_workflows: number;
  tasks_pending: number;
  tasks_completed_today: number;
  high_value_leads: number;
  emails_queued: number;
}

const WorkflowTaskQueue: React.FC = () => {
  const [tasks, setTasks] = useState<WorkflowTask[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowInstance[]>([]);
  const [stats, setStats] = useState<WorkflowStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tasks');
  const [processingTask, setProcessingTask] = useState<string | null>(null);

  useEffect(() => {
    fetchWorkflowData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchWorkflowData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchWorkflowData = async () => {
    try {
      setLoading(true);
      
      // Fetch tasks, workflows, and stats in parallel
      const [tasksRes, workflowsRes, statsRes] = await Promise.all([
        axiosInstance.get('/agent-kairos/tasks'),
        axiosInstance.get('/agent-kairos/workflows'),
        axiosInstance.get('/agent-kairos/stats')
      ]);

      setTasks(tasksRes.data.data || []);
      setWorkflows(workflowsRes.data.data || []);
      setStats(statsRes.data.data || {
        total_workflows: 0,
        active_workflows: 0,
        tasks_pending: 0,
        tasks_completed_today: 0,
        high_value_leads: 0,
        emails_queued: 0
      });
    } catch (error) {
      console.error('Error fetching workflow data:', error);
      // Use mock data for demonstration
      setStats({
        total_workflows: 57,
        active_workflows: 57,
        tasks_pending: 228,
        tasks_completed_today: 0,
        high_value_leads: 3,
        emails_queued: 57
      });
      
      // Mock tasks based on your actual data
      setTasks([
        {
          task_id: '1',
          lead_id: 'lead-1',
          lead_first_name: 'Dale',
          lead_last_name: 'Schoenberg',
          lead_email: 'dschoenberg@tds.net',
          lead_score: 78,
          agent_type: 'response',
          action: 'send_reengagement_email',
          priority: 'high',
          scheduled_for: new Date().toISOString(),
          status: 'pending',
          requires_approval: true
        },
        {
          task_id: '2',
          lead_id: 'lead-2',
          lead_first_name: 'Michele',
          lead_last_name: 'Quiroz',
          lead_email: 'lv2shopmich@aol.com',
          lead_score: 78,
          agent_type: 'response',
          action: 'send_reengagement_email',
          priority: 'high',
          scheduled_for: new Date().toISOString(),
          status: 'pending',
          requires_approval: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTask = async (taskId: string) => {
    try {
      setProcessingTask(taskId);
      await axiosInstance.post(`/agent-kairos/tasks/${taskId}/approve`);
      toast.success('Task approved and queued for execution');
      fetchWorkflowData();
    } catch (error) {
      toast.error('Failed to approve task');
    } finally {
      setProcessingTask(null);
    }
  };

  const getAgentIcon = (agentType: string) => {
    switch (agentType) {
      case 'response': return faEnvelope;
      case 'intelligence': return faChartLine;
      case 'nurture': return faUsers;
      case 'monitor': return faChartLine;
      default: return faRobot;
    }
  };

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      'send_reengagement_email': 'Re-engagement Email',
      'analyze_property_matches': 'Property Analysis',
      'send_property_matches': 'Property Matches Email',
      'schedule_consultation': 'Schedule Consultation',
      'send_market_update': 'Market Update Email'
    };
    return labels[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'secondary';
      default: return 'light';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" style={{ color: '#00d4ff' }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <div className="mt-3">Loading automation data...</div>
      </div>
    );
  }

  return (
    <div className="workflow-automation-container">
      {/* Stats Overview */}
      <Row className="g-3 mb-4">
        <Col lg={2} md={4} sm={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-2">
                <FontAwesomeIcon icon={faBolt} size="2x" style={{ color: '#00d4ff' }} />
              </div>
              <h3 className="mb-0">{stats?.active_workflows || 0}</h3>
              <small className="text-muted">Active Workflows</small>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={2} md={4} sm={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-2">
                <FontAwesomeIcon icon={faTasks} size="2x" style={{ color: '#f59e0b' }} />
              </div>
              <h3 className="mb-0">{stats?.tasks_pending || 0}</h3>
              <small className="text-muted">Pending Tasks</small>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={2} md={4} sm={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-2">
                <FontAwesomeIcon icon={faEnvelope} size="2x" style={{ color: '#10b981' }} />
              </div>
              <h3 className="mb-0">{stats?.emails_queued || 0}</h3>
              <small className="text-muted">Emails Queued</small>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={2} md={4} sm={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-2">
                <FontAwesomeIcon icon={faCheckCircle} size="2x" style={{ color: '#22c55e' }} />
              </div>
              <h3 className="mb-0">{stats?.tasks_completed_today || 0}</h3>
              <small className="text-muted">Completed Today</small>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={2} md={4} sm={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-2">
                <FontAwesomeIcon icon={faUsers} size="2x" style={{ color: '#8b5cf6' }} />
              </div>
              <h3 className="mb-0">{stats?.high_value_leads || 0}</h3>
              <small className="text-muted">High-Value Leads</small>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={2} md={4} sm={6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="mb-2">
                <ProgressBar 
                  now={(stats?.tasks_completed_today || 0) / ((stats?.tasks_pending || 0) + (stats?.tasks_completed_today || 0)) * 100} 
                  style={{ height: '8px' }}
                  variant="success"
                />
              </div>
              <h5 className="mb-0">
                {Math.round((stats?.tasks_completed_today || 0) / ((stats?.tasks_pending || 0) + (stats?.tasks_completed_today || 0)) * 100)}%
              </h5>
              <small className="text-muted">Daily Progress</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs for Tasks and Workflows */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-light border-bottom">
          <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'tasks')}>
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="tasks">
                  <FontAwesomeIcon icon={faTasks} className="me-2" />
                  Task Queue ({tasks.length})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="workflows">
                  <FontAwesomeIcon icon={faBolt} className="me-2" />
                  Active Workflows ({workflows.length})
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Tab.Container>
        </Card.Header>
        <Card.Body>
          {activeTab === 'tasks' && (
            <div className="task-queue">
              {tasks.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FontAwesomeIcon icon={faRobot} size="3x" className="mb-3" />
                  <p>No pending tasks. Automation is up to date!</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Lead</th>
                        <th>Action</th>
                        <th>Agent</th>
                        <th>Priority</th>
                        <th>Scheduled</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => (
                        <tr key={task.task_id}>
                          <td>
                            <div>
                              <strong>{task.lead_first_name} {task.lead_last_name}</strong>
                              {task.lead_score && (
                                <Badge bg="light" text="dark" className="ms-2">
                                  Score: {task.lead_score}
                                </Badge>
                              )}
                              <div className="small text-muted">{task.lead_email}</div>
                            </div>
                          </td>
                          <td>{getActionLabel(task.action)}</td>
                          <td>
                            <FontAwesomeIcon 
                              icon={getAgentIcon(task.agent_type)} 
                              className="me-2" 
                              style={{ color: '#00d4ff' }}
                            />
                            {task.agent_type}
                          </td>
                          <td>
                            <Badge bg={getPriorityColor(task.priority)}>
                              {task.priority}
                            </Badge>
                          </td>
                          <td>
                            <div className="small">
                              {format(new Date(task.scheduled_for), 'MMM dd, hh:mm a')}
                            </div>
                          </td>
                          <td>
                            <Badge 
                              bg={task.status === 'pending' ? 'warning' : 
                                 task.status === 'completed' ? 'success' : 
                                 task.status === 'failed' ? 'danger' : 'secondary'}
                            >
                              {task.status}
                            </Badge>
                          </td>
                          <td>
                            {task.requires_approval && task.status === 'pending' && (
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleApproveTask(task.task_id)}
                                disabled={processingTask === task.task_id}
                              >
                                {processingTask === task.task_id ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faPlay} className="me-1" />
                                    Approve
                                  </>
                                )}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'workflows' && (
            <div className="workflows-list">
              {workflows.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <FontAwesomeIcon icon={faBolt} size="3x" className="mb-3" />
                  <p>No active workflows. Create workflows to start automation!</p>
                </div>
              ) : (
                <Row className="g-3">
                  {workflows.map((workflow) => (
                    <Col key={workflow.instance_id} lg={4} md={6}>
                      <Card className="h-100 border-0 shadow-sm">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <h6 className="mb-1">
                                {workflow.lead_name || `${workflow.lead_first_name} ${workflow.lead_last_name}`}
                              </h6>
                              <div className="d-flex gap-2 flex-wrap">
                                <Badge bg="light" text="dark">
                                  Score: {workflow.lead_score || 'N/A'}
                                </Badge>
                                {workflow.property_type && (
                                  <Badge bg="light" text="dark">
                                    {workflow.property_type}
                                  </Badge>
                                )}
                                {workflow.buying_timeline && (
                                  <Badge bg="info">
                                    {workflow.buying_timeline}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Badge 
                              bg={workflow.status === 'ACTIVE' ? 'success' : 
                                 workflow.status === 'PAUSED' ? 'warning' : 'secondary'}
                            >
                              {workflow.status}
                            </Badge>
                          </div>

                          {/* Progress Bar */}
                          {workflow.progress_percentage !== undefined && (
                            <div className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <small className="text-muted">Progress</small>
                                <small className="text-muted">
                                  {workflow.completed_tasks || 0}/{workflow.total_tasks || 0} tasks
                                </small>
                              </div>
                              <ProgressBar 
                                now={workflow.progress_percentage} 
                                label={`${workflow.progress_percentage}%`}
                                variant="success"
                                style={{ height: '10px' }}
                              />
                            </div>
                          )}

                          {/* Upcoming Actions */}
                          {workflow.upcoming_tasks && workflow.upcoming_tasks.length > 0 && (
                            <div className="mb-3">
                              <small className="text-muted d-block mb-2">Next Actions:</small>
                              <div className="d-flex flex-column gap-1">
                                {workflow.upcoming_tasks.map((task, idx) => (
                                  <div key={idx} className="d-flex align-items-center gap-2" style={{ fontSize: '0.85rem' }}>
                                    <FontAwesomeIcon 
                                      icon={getAgentIcon(task.agent_type)} 
                                      style={{ color: '#00d4ff', width: '16px' }}
                                    />
                                    <span>{getActionLabel(task.action)}</span>
                                    <span className="text-muted ms-auto">
                                      {format(new Date(task.scheduled_for), 'MMM dd')}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Workflow Type */}
                          {workflow.workflow_template_id && (
                            <div className="mb-3">
                              <small className="text-muted">Workflow Type</small>
                              <div style={{ fontSize: '0.9rem' }}>
                                <Badge bg="secondary">
                                  {workflow.workflow_template_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </Badge>
                              </div>
                            </div>
                          )}
                          
                          <div className="mb-3">
                            <small className="text-muted">Contact Info</small>
                            <div style={{ fontSize: '0.9rem' }}>
                              {workflow.lead_email && <div className="text-truncate">{workflow.lead_email}</div>}
                              {workflow.lead_phone && <div>{workflow.lead_phone}</div>}
                            </div>
                          </div>
                          
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              <FontAwesomeIcon icon={faCalendar} className="me-1" />
                              Started {format(new Date(workflow.created_at), 'MMM dd')}
                            </small>
                            <Button 
                              size="sm" 
                              variant="outline-primary"
                              onClick={() => window.location.href = `/leads/${workflow.lead_id}`}
                            >
                              View Lead
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default WorkflowTaskQueue;